import { AnimatePresence } from "motion/react";
import { createContext, useCallback, useContext } from "react";
import * as motion from "motion/react-client";
import useScreenDimensions from "../hooks/useScreenDimensions";

import { create } from "zustand";
import { DashboardContext } from "../Pages/dashboard";

interface LinkModalState {
  isOpen: boolean;
  setIsOpen: (newVal: boolean) => void;
  link: string;
  setLink: (newLink: string) => void;
  message: string;
  setMessage: (newMessage: string) => void;
  openWithLinkAndMessage: (link: string, message: string) => void;
}

export const useLinkModal = create<LinkModalState>((set) => ({
  isOpen: false,
  setIsOpen: (newVal: boolean) => set({ isOpen: newVal }),
  link: "",
  setLink: (newLink: string) => set({ link: newLink }),
  message: "",
  setMessage: (newMessage: string) => set({ message: newMessage }),
  openWithLinkAndMessage: (link: string, message: string) =>
    set({ isOpen: true, link, message }),
}));

export const LinkModal = () => {
  const isOpen = useLinkModal((state) => state.isOpen);
  const setIsOpen = useLinkModal((state) => state.setIsOpen);
  const link = useLinkModal((state) => state.link);
  const message = useLinkModal((state) => state.message);
  const { width, height } = useScreenDimensions();
  const { openSnackBar } = useContext(DashboardContext);

  const cancelEvent = (e: React.MouseEvent) => e.stopPropagation();

  const closeModal = () => {
    setIsOpen(false);
  };

  const handleCopy = useCallback(async () => {
    try {
      if (!link) return;
      await navigator.clipboard.writeText(link);
      closeModal();
      openSnackBar();
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  }, [link]);

  return (
    <AnimatePresence>
      {isOpen ? (
        <motion.div
          key={"campaignModal"}
          style={{
            width,
            height,
            backgroundColor: "rgba(0, 0, 0, .3)",
            position: "fixed",
            zIndex: 10,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            top: 0,
            left: 0,
          }}
          onClick={closeModal}
          animate={{ opacity: 1 }}
          initial={{ opacity: 0 }}
          exit={{ opacity: 0 }}
        >
          <div
            onClick={cancelEvent}
            style={{
              width: "40rem",
              minWidth: 400,
              backgroundColor: "white",
              padding: 24,
              borderRadius: 16,
              opacity: 1,
            }}
          >
            <>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  fontSize: "1.25rem",
                  fontWeight: 500,
                  marginBottom: "1rem",
                }}
              >
                {message}
              </div>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                  fontSize: "1rem",
                  marginBottom: "1rem ",
                }}
              >
                Press the button to copy the link
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleCopy}
                style={{
                  cursor: "pointer",
                  width: "100%",
                  maxWidth: "600px",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "16px",
                    backgroundColor: "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: "16px",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    transition: "background-color 0.2s",
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = "#e5e7eb")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = "#f3f4f6")
                  }
                >
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: "#374151",
                      fontWeight: "500",
                      fontSize: "18px",
                      userSelect: "none",
                    }}
                  >
                    {link}
                  </span>
                </div>
              </motion.div>
            </>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
